import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Receipt, ArrowLeft, Download, ExternalLink, FileText, Wallet,
  Users2, Timer, TrendingUp, AlertCircle, CreditCard, Banknote,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const PERIOD_LABELS = {
  7: "7 Tage",
  30: "30 Tage",
  90: "90 Tage",
  365: "365 Tage",
};

const fmtEUR = (n) => (typeof n === "number" ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0,00");
const fmtMin = (n) => (typeof n === "number" ? n.toFixed(1) : "0.0");

const TeamBilling = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async (period) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/org/billing-summary?days=${period}`);
      setData(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) {
        setError("Nur Direktoren mit eigener Organisation können diese Seite öffnen.");
      } else {
        setError(detail || "Daten konnten nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/payments/billing-portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Customer Portal konnte nicht geöffnet werden");
    } finally {
      setPortalBusy(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get(`/org/usage/export.csv?days=${days}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `novatus-verbrauch_${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV heruntergeladen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV-Export fehlgeschlagen");
    }
  };

  if (loading && !data) {
    return <div className="p-12 text-center text-novatus-text-3">Lade Abrechnungsdaten...</div>;
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Link to="/team" className="text-novatus-text-2 hover:text-white text-sm inline-flex items-center gap-2 mb-6">
          <ArrowLeft className="w-4 h-4" /> Zurück zum Team-Dashboard
        </Link>
        <div className="nv-card p-8 text-center">
          <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="text-white font-semibold">{error}</p>
          <p className="text-novatus-text-3 text-sm mt-2">Lege zuerst eine Organisation auf dem Team-Dashboard an.</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { organization, plan, totals, by_member, invoices, has_stripe_link, available_periods } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      {/* Header */}
      <div className="animate-slide-up flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <Link to="/team" className="text-novatus-text-2 hover:text-white text-xs inline-flex items-center gap-1 mb-2" data-testid="back-to-team">
            <ArrowLeft className="w-3 h-3" /> Team-Dashboard
          </Link>
          <span className="label-eyebrow">Direktor · Abrechnung</span>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
            Verbrauch & <span className="text-novatus-green">Rechnungen.</span>
          </h1>
          <p className="text-novatus-text-2 mt-3 max-w-2xl">
            Verbrauchsreports pro Mitarbeiter, anteilige Kosten und alle Stripe-Rechnungen für <strong className="text-white">{organization.name}</strong>.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={downloadCsv}
            className="btn-ghost-green h-11 px-5 rounded-lg inline-flex items-center gap-2"
            data-testid="csv-export-btn"
          >
            <Download className="w-4 h-4" /> CSV exportieren
          </button>
          {has_stripe_link && (
            <button
              onClick={openPortal}
              disabled={portalBusy}
              className="btn-green h-11 px-5 rounded-lg inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="open-portal-btn"
            >
              <CreditCard className="w-4 h-4" /> {portalBusy ? "Öffne..." : "Stripe Portal"}
            </button>
          )}
        </div>
      </div>

      {/* Period selector */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="period-selector">
        <span className="label-eyebrow mr-2">Zeitraum:</span>
        {(available_periods || [7, 30, 90, 365]).map(p => (
          <button
            key={p}
            onClick={() => setDays(p)}
            className={`pill-tab ${days === p ? "active" : ""}`}
            data-testid={`period-${p}`}
          >
            {PERIOD_LABELS[p] || `${p} Tage`}
          </button>
        ))}
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
        <div className="nv-card p-6 nv-card-hover" data-testid="stat-pool-balance">
          <p className="label-eyebrow flex items-center gap-1.5"><Wallet className="w-3 h-3" /> Pool-Saldo</p>
          <p className="font-display font-black text-3xl text-white mt-2">{fmtMin(organization.minutes_balance)}</p>
          <p className="text-xs text-novatus-text-3 mt-1">Verfügbare Minuten</p>
        </div>
        <div className="nv-card p-6 nv-card-hover" data-testid="stat-used-period">
          <p className="label-eyebrow flex items-center gap-1.5"><Timer className="w-3 h-3" /> Verbraucht</p>
          <p className="font-display font-black text-3xl text-novatus-green mt-2">{fmtMin(totals.minutes_used)}</p>
          <p className="text-xs text-novatus-text-3 mt-1">Min · {totals.sessions} Sessions</p>
        </div>
        <div className="nv-card p-6 nv-card-hover" data-testid="stat-active-members">
          <p className="label-eyebrow flex items-center gap-1.5"><Users2 className="w-3 h-3" /> Aktiv</p>
          <p className="font-display font-black text-3xl text-white mt-2">
            {totals.active_members}<span className="text-novatus-text-3 text-xl">/{organization.members_total}</span>
          </p>
          <p className="text-xs text-novatus-text-3 mt-1">Nutzer im Zeitraum</p>
        </div>
        <div className="nv-card p-6 nv-card-hover" data-testid="stat-avg-per-member">
          <p className="label-eyebrow flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Ø pro Nutzer</p>
          <p className="font-display font-black text-3xl text-white mt-2">{fmtMin(totals.avg_minutes_per_member)}</p>
          <p className="text-xs text-novatus-text-3 mt-1">Min · {PERIOD_LABELS[days]}</p>
        </div>
      </div>

      {/* Plan card */}
      {plan && (
        <div className="mt-5 nv-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="plan-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-novatus-green/10 border border-novatus-green/40 flex items-center justify-center shrink-0">
              <Banknote className="w-5 h-5 text-novatus-green" />
            </div>
            <div>
              <p className="label-eyebrow">Aktiver Plan</p>
              <h3 className="font-display font-bold text-white text-xl">{plan.label}</h3>
              <p className="text-xs text-novatus-text-2 mt-1">
                <span className="text-white font-mono">{fmtEUR(plan.monthly_cost_eur)} €</span>/Monat
                {" · "}<span className="font-mono">{plan.minutes_per_month}</span> Min/Monat im Pool
              </p>
            </div>
          </div>
          <div className="text-sm text-novatus-text-3">
            Plan-Wechsel und Kündigung über das Stripe Portal.
          </div>
        </div>
      )}

      {/* Per-member consumption */}
      <div className="nv-card mt-5 overflow-x-auto" data-testid="member-consumption-table">
        <div className="p-4 border-b border-novatus flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users2 className="w-4 h-4 text-novatus-green" />
            <h3 className="font-display font-bold text-white">Verbrauch pro Mitarbeiter</h3>
            <span className="text-xs text-novatus-text-3">· {PERIOD_LABELS[days]}</span>
          </div>
        </div>
        {by_member.length === 0 ? (
          <div className="px-6 py-10 text-center text-novatus-text-3 text-sm">
            Im gewählten Zeitraum wurden keine Minuten verbraucht.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-novatus-card-2 border-b border-novatus">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Name</th>
                <th className="px-4 py-3 label-eyebrow text-right">Trainings</th>
                <th className="px-4 py-3 label-eyebrow text-right">Minuten</th>
                <th className="px-4 py-3 label-eyebrow text-right">Anteil</th>
                <th className="px-4 py-3 label-eyebrow text-right">Kosten (€)</th>
              </tr>
            </thead>
            <tbody>
              {by_member.map(m => (
                <tr key={m.user_id} className="border-b border-novatus hover:bg-white/[0.02]" data-testid={`member-row-${m.user_id}`}>
                  <td className="px-4 py-3 font-semibold text-white">{m.name || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-white">{m.sessions}</td>
                  <td className="px-4 py-3 text-right font-mono text-white">{fmtMin(m.minutes_used)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      <div className="w-20 h-1.5 bg-novatus rounded-full overflow-hidden hidden sm:block">
                        <div className="h-full bg-novatus-green rounded-full" style={{ width: `${Math.min(100, m.share_pct)}%` }} />
                      </div>
                      <span className="font-mono text-white">{m.share_pct}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-display font-black text-novatus-green">{fmtEUR(m.cost_share_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Invoices */}
      <div className="nv-card mt-5 overflow-x-auto" data-testid="invoices-table">
        <div className="p-4 border-b border-novatus flex items-center gap-2">
          <Receipt className="w-4 h-4 text-novatus-green" />
          <h3 className="font-display font-bold text-white">Rechnungen</h3>
          <span className="text-xs text-novatus-text-3">· über Stripe</span>
        </div>
        {!has_stripe_link ? (
          <div className="px-6 py-10 text-center text-novatus-text-3 text-sm">
            Noch kein Stripe-Kunde verknüpft. Beim ersten Abo-Kauf wird automatisch ein Kunde angelegt.
          </div>
        ) : invoices.length === 0 ? (
          <div className="px-6 py-10 text-center text-novatus-text-3 text-sm">
            Noch keine Rechnungen vorhanden.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-novatus-card-2 border-b border-novatus">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Datum</th>
                <th className="px-4 py-3 label-eyebrow">Nr.</th>
                <th className="px-4 py-3 label-eyebrow">Bezeichnung</th>
                <th className="px-4 py-3 label-eyebrow">Status</th>
                <th className="px-4 py-3 label-eyebrow text-right">Betrag (€)</th>
                <th className="px-4 py-3 label-eyebrow text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-b border-novatus hover:bg-white/[0.02]" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 text-novatus-text-2 text-xs whitespace-nowrap">
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString("de-DE") : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-novatus-text-2 text-xs">{inv.number || "—"}</td>
                  <td className="px-4 py-3 text-white">{inv.label}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${
                      inv.status === "paid"
                        ? "bg-novatus-green/10 text-novatus-green border-novatus-green/40"
                        : "bg-amber-400/10 text-amber-300 border-amber-400/40"
                    }`}>
                      {inv.status || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-display font-black text-white">{fmtEUR(inv.amount_eur)}</td>
                  <td className="px-4 py-3 text-right">
                    {inv.invoice_pdf && (
                      <a
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-novatus-green hover:underline text-xs"
                        data-testid={`invoice-pdf-${inv.id}`}
                      >
                        <FileText className="w-3 h-3" /> PDF
                      </a>
                    )}
                    {!inv.invoice_pdf && inv.invoice_url && (
                      <a
                        href={inv.invoice_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-novatus-text-2 hover:text-white text-xs"
                      >
                        <ExternalLink className="w-3 h-3" /> Anzeigen
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TeamBilling;
